export interface MeResponse {
  email: string;
  displayName: string | null;
  /**
   * Currently-active customer. Equals `homeCustomer` for non-global users;
   * may point to any other active tenant when a global user has switched
   * into it.
   */
  customer: { slug: string; id: string; displayName: string | null };
  /**
   * Tenant the user originally signed in from. Preserved across active-
   * customer switches so the UI can label cross-tenant activity and offer
   * a "return home" affordance.
   */
  homeCustomer: { slug: string; id: string; displayName: string | null };
  canViewAuditDashboard: boolean;
  canReviewDocuments: boolean;
  /**
   * Whether the user's home customer is marked `isGlobal`. Drives the
   * customer picker (landing screen + header dropdown).
   */
  isGlobalUser: boolean;
  /**
   * False immediately after a global user signs in (the SPA routes them
   * to the picker until they make an explicit choice); pre-set to true
   * for non-global users so they skip the picker step entirely.
   */
  hasPickedActiveCustomer: boolean;
}

export interface AvailableCustomer {
  id: string;
  slug: string;
  displayName: string;
  /** Whether this entry is the caller's home tenant. */
  isHome: boolean;
}

export type ReviewStatus = 'draft' | 'in_review' | 'approved';

/**
 * Per-reviewer vote value. `no_response` is the sentinel for "reviewer
 * hasn't voted yet" and is also the value the client sends to clear an
 * existing vote.
 */
export type ReviewVoteValue = ReviewStatus | 'no_response';

export interface ReviewerVote {
  portalUserId: string;
  email: string;
  displayName: string | null;
  status: ReviewVoteValue;
  updatedAt: string | null;
}

export interface ReviewStatusResponse {
  /**
   * Whether the document is eligible for review at all. The server returns
   * `false` when the path matches an admin-configured exclusion pattern
   * (`GitlabConfig.reviewExcludedPatterns`); in that case `reviewers` is
   * empty and the SPA hides both the status pill and the reviewers popover.
   */
  reviewable: boolean;
  /**
   * Derived from the set of reviewer votes (any approved -> approved;
   * otherwise any draft -> draft; otherwise in_review). Drives the tree
   * dot color and the viewer toolbar pill.
   */
  overall: ReviewStatus;
  /** Caller's personal vote. `no_response` if they have not voted yet. */
  myStatus: ReviewVoteValue;
  /** Whether the caller has the reviewer role for this customer. */
  canEdit: boolean;
  reviewers: ReviewerVote[];
}

export type AuditAction = 'view' | 'download' | 'pdf' | 'proxy_view';

export interface AuditEvent {
  id: string;
  email: string;
  displayName: string | null;
  path: string;
  action: AuditAction;
  createdAt: string;
  /**
   * Slug of the tenant the actor originally signed in from. When it
   * differs from the dashboard's active customer slug, the event came
   * from a global user impersonating this tenant.
   */
  homeCustomerSlug: string;
  homeCustomerDisplayName: string;
}

export interface AuditEventsPage {
  total: number;
  page: number;
  pageSize: number;
  items: AuditEvent[];
}

export interface AuditSummary {
  totals: {
    views: number;
    downloads: number;
    pdfs: number;
    proxyViews: number;
    uniqueUsers: number;
    uniqueDocuments: number;
    avgEngagementMs: number | null;
  };
  topDocuments: Array<{ path: string; views: number; downloads: number; totalMs: number }>;
  topUsers: Array<{
    email: string;
    displayName: string | null;
    views: number;
    downloads: number;
    totalMs: number;
    /** See {@link AuditEvent.homeCustomerSlug}. */
    homeCustomerSlug: string;
    homeCustomerDisplayName: string;
  }>;
}

export interface AuditEventsFilters {
  from?: string;
  to?: string;
  email?: string;
  path?: string;
  action?: AuditAction;
  page?: number;
  pageSize?: number;
}

export interface AuditEngagementRow {
  portalUserId: string;
  email: string;
  displayName: string | null;
  path: string;
  totalMs: number;
  sessions: number;
  lastSeenAt: string;
  /** See {@link AuditEvent.homeCustomerSlug}. */
  homeCustomerSlug: string;
  homeCustomerDisplayName: string;
}

export interface AuditEngagementPage {
  total: number;
  page: number;
  pageSize: number;
  items: AuditEngagementRow[];
}

export interface AuditEngagementFilters {
  from?: string;
  to?: string;
  email?: string;
  path?: string;
  page?: number;
  pageSize?: number;
}

export interface TreeEntry {
  // NOTE: the server's CacheEntry has no `id` — `path` is the unique
  // identifier inside a folder listing, so use it as the React key.
  name: string;
  type: 'tree' | 'blob';
  path: string;
  /**
   * Whether this blob is eligible for the review workflow. The server sets
   * this to `false` when the path matches an admin-configured exclusion
   * pattern; missing (legacy) responses are treated as reviewable.
   */
  reviewable?: boolean;
  /**
   * Review status surfaced by the server for blob entries. Folders never
   * carry a status; excluded blobs (reviewable === false) never carry one
   * either. Missing on legacy responses, in which case the UI falls back
   * to the default `in_review`.
   */
  reviewStatus?: ReviewStatus;
}

export interface TreeResponse {
  path: string;
  entries: TreeEntry[];
}

export type PageResponse =
  | { path: string; type: 'markdown'; html: string; title: string | null }
  | { path: string; type: 'html'; iframeUrl: string; title: null };

export type CacheStatus = 'empty' | 'fetching' | 'ok' | 'error';

export type ContentSource =
  | { mode: 'repo'; cacheStatus: CacheStatus }
  | { mode: 'pages'; entryUrl: string; cacheStatus: CacheStatus }
  | { mode: 'unconfigured' };

const json = async <T>(input: RequestInfo, init?: RequestInit): Promise<T> => {
  const res = await fetch(input, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body || res.statusText);
  }
  return (await res.json()) as T;
};

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

export type DiscoverResult =
  | { flow: 'oidc'; loginUrl: string }
  | { flow: 'email_code' }
  | { flow: null };

export const api = {
  discover: (email: string) =>
    json<DiscoverResult>('/api/v1/auth/discover', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }),
  requestEmailCode: (email: string) =>
    fetch('/api/v1/auth/email/request', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }).then((res) => {
      if (!res.ok) throw new ApiError(res.status, res.statusText);
    }),
  verifyEmailCode: (email: string, code: string) =>
    json<MeResponse>('/api/v1/auth/email/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    }),
  me: () => json<MeResponse>('/api/v1/me'),
  logout: () =>
    fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'same-origin' }),
  source: () => json<ContentSource>('/api/v1/content/source'),
  tree: (path: string) =>
    json<TreeResponse>(`/api/v1/content/tree?path=${encodeURIComponent(path)}`),
  page: (path: string) =>
    json<PageResponse>(`/api/v1/content/page?path=${encodeURIComponent(path)}`),
  fileUrl: (path: string) => `/api/v1/content/file?path=${encodeURIComponent(path)}`,
  pdfUrl: (path: string) => `/api/v1/content/pdf?path=${encodeURIComponent(path)}`,
  pdfAllowed: (path: string) =>
    json<{ allowed: boolean }>(
      `/api/v1/content/pdf-allowed?path=${encodeURIComponent(path)}`,
    ),
  availableCustomers: () =>
    json<{ customers: AvailableCustomer[] }>('/api/v1/me/available-customers'),
  setActiveCustomer: async (customerId: string): Promise<void> => {
    const res = await fetch('/api/v1/me/active-customer', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId }),
    });
    if (!res.ok) {
      const body = await res.text();
      throw new ApiError(res.status, body || res.statusText);
    }
  },
};

export const reviewApi = {
  getStatus: (path: string) =>
    json<ReviewStatusResponse>(
      `/api/v1/review/status?path=${encodeURIComponent(path)}`,
    ),
  setStatus: (path: string, status: ReviewVoteValue) =>
    json<ReviewStatusResponse>(
      `/api/v1/review/status?path=${encodeURIComponent(path)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      },
    ),
};

export const auditApi = {
  startHeartbeat: (path: string) =>
    json<{ token: string }>('/api/v1/audit/heartbeat/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path }),
    }),
  heartbeat: (token: string, deltaMs: number) =>
    fetch('/api/v1/audit/heartbeat', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, deltaMs }),
    }),
  closeHeartbeat: (token: string, deltaMs: number) => {
    const payload = JSON.stringify({ token, deltaMs });
    // sendBeacon survives a navigation/unload where fetch() would be cancelled.
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([payload], { type: 'application/json' });
      const queued = navigator.sendBeacon('/api/v1/audit/heartbeat/close', blob);
      if (queued) return Promise.resolve();
    }
    return fetch('/api/v1/audit/heartbeat/close', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    });
  },
  history: (path: string, limit = 20) =>
    json<{ items: AuditEvent[] }>(
      `/api/v1/audit/history?path=${encodeURIComponent(path)}&limit=${limit}`,
    ),
  events: (filters: AuditEventsFilters) =>
    json<AuditEventsPage>(
      `/api/v1/audit/events?${buildQuery({ ...filters } as Record<string, string | number | undefined>)}`,
    ),
  summary: (from?: string, to?: string) =>
    json<AuditSummary>(
      `/api/v1/audit/summary?${buildQuery({ from, to })}`,
    ),
  engagements: (filters: AuditEngagementFilters) =>
    json<AuditEngagementPage>(
      `/api/v1/audit/engagements?${buildQuery({ ...filters } as Record<string, string | number | undefined>)}`,
    ),
};

function buildQuery(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === null) continue;
    qs.set(key, String(value));
  }
  return qs.toString();
}
